import type { ReactNode } from 'react';
import {
  Alert,
  Button,
  Col,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SettingOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserAddOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { getUnifiedTasks, type UnifiedTaskRow } from '@/services/unifiedTask.service';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  buildAttentionCounts,
  type AttendanceExceptionBoard,
} from '@/pages/education/utils/educationHomeAttention';

const { Text, Paragraph } = Typography;

type EducationShell = {
  module: string;
  pack_key: string;
  version: string;
};

type ShellError = {
  response?: { status?: number; data?: { message?: string; error?: string } };
};

type SchoolSettingsPayload = {
  company: { id: string; name: string; code: string };
  education_profile: Record<string, unknown>;
  profile_configured: boolean;
  campuses?: Array<{ id: string }>;
};

type AcademicSession = {
  id: string;
  name?: string;
  code?: string;
  status?: string;
  is_active?: number | boolean;
};

type CampusRow = { id: string };
type ListRow = { id: string; status?: string };

function asList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

type LaunchStatus = 'ready' | 'setup' | 'restricted';

type LaunchCard = {
  key: string;
  path: string;
  title: string;
  description: string;
  icon: ReactNode;
  status: LaunchStatus;
};

function launchStatus(allowed: boolean, setupIncomplete = false): LaunchStatus {
  if (!allowed) return 'restricted';
  return setupIncomplete ? 'setup' : 'ready';
}

export default function EducationHomePage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const user = useAuthStore((s) => s.user);
  const companyId = user?.company_id;
  const permissions = user?.permissions ?? [];
  const superAdmin = isPlatformSuperAdmin(user);
  const can = (path: string) => canAccessRouteByPermissions(path, permissions, superAdmin);

  const educationPermCount = permissions.filter((p) => String(p).startsWith('education.')).length;

  const shellQuery = useQuery({
    queryKey: ['education-shell'],
    queryFn: () => api.get<EducationShell>('/education'),
    retry: false,
  });

  const companyQuery = useQuery({
    queryKey: ['education-settings'],
    enabled: can('/education/settings') || Boolean(companyId),
    queryFn: async () => (await api.get<SchoolSettingsPayload>('/education/settings')).data,
    retry: false,
  });

  const campusesQuery = useQuery({
    queryKey: ['education-home-campuses'],
    enabled: can('/education/students'),
    queryFn: async () => asList<CampusRow>((await api.get('/education/campuses')).data),
    retry: false,
  });

  const sessionsQuery = useQuery({
    queryKey: ['education-home-sessions'],
    enabled: can('/education/academic'),
    queryFn: async () => asList<AcademicSession>((await api.get('/education/academic/sessions')).data),
    retry: false,
  });

  const studentsQuery = useQuery({
    queryKey: ['education-home-students'],
    enabled: can('/education/students'),
    queryFn: async () => asList<ListRow>((await api.get('/education/students')).data),
    retry: false,
  });

  const admissionsQuery = useQuery({
    queryKey: ['education-home-admissions'],
    enabled: can('/education/admissions'),
    queryFn: async () => asList<ListRow>((await api.get('/education/admissions')).data),
    retry: false,
  });

  const enrollmentsQuery = useQuery({
    queryKey: ['education-home-enrollments'],
    enabled: can('/education/enrollments'),
    queryFn: async () => asList<ListRow>((await api.get('/education/enrollments')).data),
    retry: false,
  });

  const attendanceQuery = useQuery({
    queryKey: ['education-home-attendance'],
    enabled: can('/education/attendance'),
    queryFn: async () => asList<ListRow>((await api.get('/education/attendance/registers')).data),
    retry: false,
  });

  const today = dayjs().format('YYYY-MM-DD');
  const exceptionsQuery = useQuery({
    queryKey: ['education-home-exceptions', today],
    enabled: can('/education/attendance/exceptions') || can('/education/attendance'),
    queryFn: async () =>
      (await api.get<AttendanceExceptionBoard>('/education/attendance/exceptions', {
        params: { attendance_date: today },
      })).data,
    retry: false,
  });

  const tasksQuery = useQuery({
    queryKey: ['education-home-unified-tasks'],
    queryFn: async () => {
      try {
        return await getUnifiedTasks({
          assigned_to: 'me',
          open_only: true,
          limit: 8,
          offset: 0,
        });
      } catch {
        return [] as UnifiedTaskRow[];
      }
    },
    retry: false,
  });

  const shell = shellQuery.data?.data;
  const err = shellQuery.error as ShellError | null;
  const schoolSettings = companyQuery.data;
  const profileConfigured = Boolean(schoolSettings?.profile_configured);
  const sessions = sessionsQuery.data ?? [];
  const activeSession =
    sessions.find((s) => s.status === 'active' || s.is_active === 1 || s.is_active === true) || null;
  const campuses = campusesQuery.data ?? [];
  const academicSetupIncomplete =
    (sessionsQuery.isSuccess && sessions.length === 0) ||
    (campusesQuery.isSuccess && campuses.length === 0);

  const openAdmissions = (admissionsQuery.data ?? []).filter((r) =>
    ['draft', 'submitted', 'verification', 'assessment', 'decision_pending', 'offer_issued', 'waitlisted'].includes(
      String(r.status || '')
    )
  ).length;
  const openEnrollments = (enrollmentsQuery.data ?? []).filter((r) =>
    ['draft', 'pending', 'submitted', 'awaiting_approval'].includes(String(r.status || '').toLowerCase())
  ).length;

  const attention = buildAttentionCounts(exceptionsQuery.data, tasksQuery.data?.length ?? 0);
  const exceptionPreview = [
    ...(exceptionsQuery.data?.unmarked ?? []).slice(0, 3).map((row, index) => ({
      key: `u-${index}`,
      kind: 'unmarked' as const,
      label: `${(row as { grade_code?: string; section_name?: string }).grade_code || ''} ${(row as { section_name?: string }).section_name || ''}`.trim() || t('unmarked'),
      path: (row as { action_path?: string }).action_path || `/education/attendance/exceptions?date=${today}`,
    })),
    ...(exceptionsQuery.data?.unmarked_periods ?? []).slice(0, 3).map((row, index) => ({
      key: `p-${index}`,
      kind: 'period' as const,
      label: `${(row as { section_name?: string }).section_name || ''} · ${(row as { period_code?: string }).period_code || ''}`.trim(),
      path: (row as { action_path?: string }).action_path || `/education/attendance/exceptions?date=${today}`,
    })),
    ...(exceptionsQuery.data?.corrections ?? []).slice(0, 3).map((row, index) => ({
      key: `c-${index}`,
      kind: 'correction' as const,
      label: `${(row as { admission_no?: string }).admission_no || (row as { student_id?: string }).student_id || ''} → ${(row as { requested_status?: string }).requested_status || ''}`.trim(),
      path: '/education/attendance/corrections',
    })),
  ].slice(0, 6);

  const refetchAll = () => {
    shellQuery.refetch();
    companyQuery.refetch();
    campusesQuery.refetch();
    sessionsQuery.refetch();
    studentsQuery.refetch();
    admissionsQuery.refetch();
    enrollmentsQuery.refetch();
    attendanceQuery.refetch();
    exceptionsQuery.refetch();
    tasksQuery.refetch();
  };

  const setupItems: Array<{ key: string; label: string; ok: boolean | null; path?: string }> = [
    {
      key: 'profile',
      label: t('setupMissingProfile'),
      ok: companyQuery.isError
        ? false
        : companyQuery.isSuccess
          ? profileConfigured
          : companyQuery.isFetching
            ? null
            : false,
      path: '/education/settings',
    },
    {
      key: 'campus',
      label: t('hubSetupCampus'),
      ok: !can('/education/students')
        ? false
        : campusesQuery.isError
          ? false
          : campusesQuery.isFetched
            ? campuses.length > 0
            : campusesQuery.isFetching
              ? null
              : false,
      path: '/education/students',
    },
    {
      key: 'session',
      label: t('setupMissingAcademic'),
      ok: !can('/education/academic')
        ? false
        : sessionsQuery.isError
          ? false
          : sessionsQuery.isFetched
            ? sessions.length > 0
            : sessionsQuery.isFetching
              ? null
              : false,
      path: '/education/academic',
    },
    {
      key: 'active',
      label: t('setupMissingActiveSession'),
      ok: !can('/education/academic')
        ? false
        : sessionsQuery.isError
          ? false
          : sessionsQuery.isFetched
            ? sessions.length === 0
              ? false
              : Boolean(activeSession)
            : sessionsQuery.isFetching
              ? null
              : false,
      path: '/education/academic',
    },
  ];

  const launchCards: LaunchCard[] = (
    [
      {
        key: 'students',
        path: '/education/students',
        title: t('students'),
        description: t('studentsIntro'),
        icon: <TeamOutlined />,
        status: launchStatus(can('/education/students'), academicSetupIncomplete),
      },
      {
        key: 'admissions',
        path: '/education/admissions',
        title: t('admissionsTitle'),
        description: t('admissionsIntro'),
        icon: <UserAddOutlined />,
        status: launchStatus(can('/education/admissions'), academicSetupIncomplete),
      },
      {
        key: 'enrollments',
        path: '/education/enrollments',
        title: t('enrollmentsTitle', { defaultValue: 'Enrollment' }),
        description: t('enrollmentsIntro'),
        icon: <SolutionOutlined />,
        status: launchStatus(can('/education/enrollments'), academicSetupIncomplete),
      },
      {
        key: 'attendance',
        path: '/education/attendance',
        title: t('openAttendance'),
        description: t('attendanceIntro'),
        icon: <CheckCircleOutlined />,
        status: launchStatus(can('/education/attendance'), academicSetupIncomplete),
      },
      {
        key: 'workspace',
        path: '/education/workspace',
        title: t('teacherWorkspace'),
        description: t('teacherWorkspaceIntro'),
        icon: <SolutionOutlined />,
        status: launchStatus(can('/education/workspace')),
      },
      {
        key: 'academic',
        path: '/education/academic',
        title: t('academicTitle'),
        description: t('academicIntro'),
        icon: <CalendarOutlined />,
        status: launchStatus(can('/education/academic')),
      },
      {
        key: 'tc',
        path: '/education/transfer-certificates',
        title: t('transferCertificateTitle', { defaultValue: 'Transfer certificates' }),
        description: t('transferCertificateIntro'),
        icon: <FileProtectOutlined />,
        status: launchStatus(can('/education/transfer-certificates'), academicSetupIncomplete),
      },
      {
        key: 'reports',
        path: '/education/reports',
        title: t('educationReports', { defaultValue: 'Education reports' }),
        description: t('educationReportsIntro', {
          defaultValue: 'Section strength, attendance summary, and fee aging via the platform report catalogue.',
        }),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/reports'), academicSetupIncomplete),
      },
      {
        key: 'behaviour',
        path: '/education/behaviour',
        title: t('behaviour'),
        description: t('behaviourIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/behaviour'), academicSetupIncomplete),
      },
      {
        key: 'inclusion',
        path: '/education/inclusion',
        title: t('inclusion'),
        description: t('inclusionIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/inclusion'), academicSetupIncomplete),
      },
      {
        key: 'intervention',
        path: '/education/intervention',
        title: t('intervention'),
        description: t('interventionIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/intervention'), academicSetupIncomplete),
      },
      {
        key: 'standards',
        path: '/education/standards',
        title: t('standards'),
        description: t('standardsIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/standards')),
      },
      {
        key: 'governance',
        path: '/education/governance',
        title: t('governance'),
        description: t('governanceIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/governance')),
      },
      {
        key: 'app',
        path: '/education/app',
        title: t('appPlans'),
        description: t('appPlansIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/app'), academicSetupIncomplete),
      },
      {
        key: 'eqams',
        path: '/education/eqams',
        title: t('eqams'),
        description: t('eqamsIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/eqams')),
      },
      {
        key: 'parentPortal',
        path: '/education/portal/parent',
        title: t('parentPortal'),
        description: t('parentPortalIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/portal/parent')),
      },
      {
        key: 'studentPortal',
        path: '/education/portal/student',
        title: t('studentPortal'),
        description: t('studentPortalIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/portal/student')),
      },
      {
        key: 'teacherPortal',
        path: '/education/portal/teacher',
        title: t('teacherPortal'),
        description: t('teacherPortalIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/portal/teacher')),
      },
      {
        key: 'udise',
        path: '/education/udise',
        title: t('udise'),
        description: t('udiseIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/udise')),
      },
      {
        key: 'ids',
        path: '/education/ids',
        title: t('ids'),
        description: t('idsIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/ids')),
      },
      {
        key: 'comms',
        path: '/education/comms',
        title: t('comms'),
        description: t('commsIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/comms')),
      },
      {
        key: 'print',
        path: '/education/print',
        title: t('printHub'),
        description: t('printHubIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/print'), academicSetupIncomplete),
      },
      {
        key: 'pulse',
        path: '/education/pulse',
        title: t('principalPulse'),
        description: t('principalPulseIntro'),
        icon: <SolutionOutlined />,
        status: launchStatus(can('/education/pulse')),
      },
      {
        key: 'quality-copilot',
        path: '/education/quality-copilot',
        title: t('qualityCopilot', { defaultValue: 'Quality Copilot' }),
        description: t('qualityCopilotIntro', { defaultValue: 'Draft-only quality wording from readiness facts.' }),
        icon: <SolutionOutlined />,
        status: launchStatus(can('/education/quality-copilot')),
      },
      {
        key: 'process-insights',
        path: '/education/process-insights',
        title: t('processInsights', { defaultValue: 'Process insights' }),
        description: t('processInsightsAdvisory', {
          defaultValue: 'Advisory transition and dwell insights for school workflows.',
        }),
        icon: <SolutionOutlined />,
        status: launchStatus(can('/education/process-insights')),
      },
      {
        key: 'fees',
        path: '/education/fees',
        title: t('fees'),
        description: t('feesIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/fees'), academicSetupIncomplete),
      },
      {
        key: 'transport',
        path: '/education/transport',
        title: t('transport'),
        description: t('transportIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/transport'), academicSetupIncomplete),
      },
      {
        key: 'library',
        path: '/education/library',
        title: t('library'),
        description: t('libraryIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/library'), academicSetupIncomplete),
      },
      {
        key: 'activities',
        path: '/education/activities',
        title: t('activities'),
        description: t('activitiesIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/activities'), academicSetupIncomplete),
      },
      {
        key: 'hpc',
        path: '/education/hpc',
        title: 'Holistic Progress Card',
        description: 'Student progress stages with text and URL evidence links.',
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/hpc'), academicSetupIncomplete),
      },
      {
        key: 'lms',
        path: '/education/lms',
        title: t('lms', { defaultValue: 'Classroom (LMS)' }),
        description: t('lmsHomeCardDescription', {
          defaultValue: 'Lesson plans, homework with feedback, resource links, and class announcements.',
        }),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/lms'), academicSetupIncomplete),
      },
      {
        key: 'timetable',
        path: '/education/timetable',
        title: t('timetable'),
        description: t('timetableIntro'),
        icon: <CalendarOutlined />,
        status: launchStatus(can('/education/timetable'), academicSetupIncomplete),
      },
      {
        key: 'substitutions',
        path: '/education/substitutions',
        title: t('substitutions'),
        description: t('substitutionsIntro'),
        icon: <TeamOutlined />,
        status: launchStatus(can('/education/substitutions'), academicSetupIncomplete),
      },
      {
        key: 'curriculum',
        path: '/education/curriculum',
        title: t('curriculum'),
        description: t('curriculumIntro'),
        icon: <FileTextOutlined />,
        status: launchStatus(can('/education/curriculum'), academicSetupIncomplete),
      },
      {
        key: 'policy',
        path: '/education/policy',
        title: t('policyTitle', { defaultValue: 'CBSE policy' }),
        description: t('policyIntro'),
        icon: <SettingOutlined />,
        status: launchStatus(can('/education/policy')),
      },
      {
        key: 'settings',
        path: '/education/settings',
        title: t('settingsTitle'),
        description: t('settingsIntro'),
        icon: <SettingOutlined />,
        status: launchStatus(can('/education/settings')),
      },
    ] satisfies LaunchCard[]
  ).filter((c) => c.status !== 'restricted');

  const loading =
    shellQuery.isLoading ||
    companyQuery.isLoading ||
    (can('/education/academic') && sessionsQuery.isLoading);

  return (
    <EducationPageChrome
      title={t('hubTitle')}
      description={t('overviewIntro')}
      tags={
        <>
          {shell ? <Tag color="processing">{shell.pack_key} v{shell.version}</Tag> : null}
          <Tag>{t('hubScopeTag')}</Tag>
          {activeSession ? (
            <Tag color="success">
              {t('activeSessionTitle')}: {activeSession.name || activeSession.code}
            </Tag>
          ) : (
            <Tag color="warning">{t('hubNoActiveSession')}</Tag>
          )}
          {schoolSettings?.company ? (
            <Tag>
              {schoolSettings.company.name}
              {schoolSettings.company.code ? ` · ${schoolSettings.company.code}` : ''}
            </Tag>
          ) : null}
        </>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={refetchAll}>
          {t('hubRefresh')}
        </Button>
      }
    >
      {shellQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('unavailable', { status: err?.response?.status ?? '' })}
          description={[err?.response?.data?.error, err?.response?.data?.message].filter(Boolean).join(' — ')}
        />
      ) : null}

      {educationPermCount < 8 ? (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={t('hubLimitedClaimsTitle')}
          description={
            superAdmin && educationPermCount === 0
              ? t('hubSuperAdminNoEducationClaims')
              : t('hubLimitedClaimsBody')
          }
        />
      ) : null}

      {academicSetupIncomplete ? (
        <Alert type="warning" showIcon message={t('setupIncompleteBanner')} />
      ) : null}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <ControlTowerSectionCard zone="attention" title={t('hubAttentionTitle')}>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('hubAttentionComposeNote')}
            </Paragraph>
            <Row gutter={[16, 16]}>
              {can('/education/attendance') || can('/education/attendance/exceptions') ? (
                <Col xs={12} sm={8} md={6}>
                  <Statistic title={t('hubStatExceptionsToday')} value={attention.exceptionTotal} />
                </Col>
              ) : null}
              <Col xs={12} sm={8} md={6}>
                <Statistic title={t('hubStatOpenTasks')} value={attention.openTasks} />
              </Col>
              {can('/education/admissions') ? (
                <Col xs={12} sm={8} md={6}>
                  <Statistic title={t('hubStatOpenAdmissions')} value={openAdmissions} />
                </Col>
              ) : null}
              {can('/education/enrollments') ? (
                <Col xs={12} sm={8} md={6}>
                  <Statistic title={t('hubStatOpenEnrollments')} value={openEnrollments} />
                </Col>
              ) : null}
              {can('/education/students') ? (
                <Col xs={12} sm={8} md={6}>
                  <Statistic title={t('hubStatStudents')} value={studentsQuery.data?.length ?? 0} />
                </Col>
              ) : null}
              {can('/education/attendance') ? (
                <Col xs={12} sm={8} md={6}>
                  <Statistic title={t('hubStatRegisters')} value={attendanceQuery.data?.length ?? 0} />
                </Col>
              ) : null}
            </Row>
          </ControlTowerSectionCard>

          <ControlTowerSectionCard zone="neutral" title={t('setupChecklistTitle')}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {setupItems.map((item) => (
                <Space key={item.key} style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                  <Text>{item.label}</Text>
                  <Space>
                    {item.ok === null ? (
                      <Tag>{t('hubStatusUnknown')}</Tag>
                    ) : item.ok ? (
                      <Tag color="success">{t('hubStatusReady')}</Tag>
                    ) : (
                      <Tag color="warning">{t('hubStatusMissing')}</Tag>
                    )}
                    {item.path && can(item.path) && item.ok === false ? (
                      <Button type="link" size="small" onClick={() => navigate(item.path!)}>
                        {t('hubConfigure')}
                      </Button>
                    ) : null}
                  </Space>
                </Space>
              ))}
            </Space>
          </ControlTowerSectionCard>

          <ControlTowerSectionCard zone="actions" title={t('hubQueuesTitle')}>
            <Row gutter={[16, 16]}>
              {can('/education/attendance') || can('/education/attendance/exceptions') ? (
                <Col xs={24} lg={12}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                    <Text strong>{t('hubExceptionStripTitle')}</Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => navigate(`/education/attendance/exceptions?date=${today}`)}
                    >
                      {t('attendanceExceptions')}
                    </Button>
                  </Space>
                  <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                    {t('hubExceptionStripHint', {
                      unmarked: attention.unmarked,
                      periods: attention.unmarkedPeriods,
                      corrections: attention.corrections,
                      unexplained: attention.unexplained,
                      repeated: attention.repeated,
                    })}
                  </Paragraph>
                  {exceptionPreview.length === 0 ? (
                    <EmptyQueueState
                      message={t('hubExceptionStripEmpty')}
                      actionLabel={t('openAttendance')}
                      onAction={() => navigate('/education/attendance')}
                    />
                  ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      {exceptionPreview.map((item) => (
                        <Space key={item.key} style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                          <Space>
                            <Tag>{item.kind}</Tag>
                            <Text>{item.label}</Text>
                          </Space>
                          <Button size="small" type="link" onClick={() => navigate(item.path)}>
                            {t('open')}
                          </Button>
                        </Space>
                      ))}
                    </Space>
                  )}
                </Col>
              ) : null}
              <Col xs={24} lg={12}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                  <Text strong>{t('hubMyTasksTitle')}</Text>
                  <Button type="link" size="small" onClick={() => navigate('/home/tasks')}>
                    {t('openMyTasks', { defaultValue: 'Open my tasks' })}
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  loading={tasksQuery.isLoading}
                  style={{ marginTop: 8 }}
                  dataSource={tasksQuery.data ?? []}
                  locale={{ emptyText: t('hubMyTasksEmpty') }}
                  columns={[
                    {
                      title: t('task', { defaultValue: 'Task' }),
                      render: (_: unknown, row: UnifiedTaskRow) =>
                        row.recommended_action ||
                        row.source_document_number ||
                        `${row.module || ''} · ${row.task_type || ''}`.trim(),
                    },
                    {
                      title: t('status'),
                      dataIndex: 'status',
                      width: 100,
                      render: (v: string) => <Tag>{v}</Tag>,
                    },
                    {
                      title: t('actions'),
                      width: 80,
                      render: (_: unknown, row: UnifiedTaskRow) => (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => navigate(row.action_link || `/home/tasks/${row.id}`)}
                        >
                          {t('open')}
                        </Button>
                      ),
                    },
                  ]}
                />
              </Col>
              {can('/education/admissions') ? (
                <Col xs={24} md={8}>
                  <Text strong>{t('admissionsTitle')}</Text>
                  {openAdmissions === 0 ? (
                    <EmptyQueueState
                      message={t('hubQueueEmptyAdmissions')}
                      actionLabel={t('newApplication')}
                      onAction={() => navigate('/education/admissions/new')}
                    />
                  ) : (
                    <EmptyQueueState
                      message={t('hubQueueOpenAdmissions', { count: openAdmissions })}
                      actionLabel={t('openAdmissions')}
                      onAction={() => navigate('/education/admissions')}
                    />
                  )}
                </Col>
              ) : null}
              {can('/education/enrollments') ? (
                <Col xs={24} md={8}>
                  <Text strong>{t('enrollmentsTitle', { defaultValue: 'Enrollment' })}</Text>
                  {openEnrollments === 0 ? (
                    <EmptyQueueState
                      message={t('hubQueueEmptyEnrollments')}
                      actionLabel={t('openEnrollments')}
                      onAction={() => navigate('/education/enrollments')}
                    />
                  ) : (
                    <EmptyQueueState
                      message={t('hubQueueOpenEnrollments', { count: openEnrollments })}
                      actionLabel={t('openEnrollments')}
                      onAction={() => navigate('/education/enrollments')}
                    />
                  )}
                </Col>
              ) : null}
              {can('/education/fees/outstanding') ? (
                <Col xs={24} md={8}>
                  <Text strong>{t('feeOutstanding')}</Text>
                  <EmptyQueueState
                    message={t('hubQueueFeesOutstanding')}
                    actionLabel={t('feeOutstanding')}
                    onAction={() => navigate('/education/fees/outstanding')}
                  />
                </Col>
              ) : null}
              {!can('/education/admissions') &&
              !can('/education/enrollments') &&
              !can('/education/attendance') &&
              !can('/education/fees/outstanding') ? (
                <Col span={24}>
                  <EmptyQueueState message={t('hubQueuesRestricted')} />
                </Col>
              ) : null}
            </Row>
          </ControlTowerSectionCard>

          <ControlTowerSectionCard zone="myTasks" title={t('authorizedAreasTitle')}>
            <Row gutter={[16, 16]}>
              {launchCards.map((card) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={card.key}>
                  <button
                    type="button"
                    onClick={() => navigate(card.path)}
                    style={{
                      width: '100%',
                      textAlign: 'start',
                      cursor: 'pointer',
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: token.borderRadiusLG,
                      background: token.colorBgContainer,
                      padding: 16,
                      minHeight: 132,
                    }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 18, color: token.colorPrimary }}>{card.icon}</span>
                        {card.status === 'setup' ? (
                          <Tag color="warning">{t('hubStatusSetup')}</Tag>
                        ) : (
                          <Tag color="success">{t('hubStatusReady')}</Tag>
                        )}
                      </Space>
                      <Text strong>{card.title}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {card.description}
                      </Text>
                    </Space>
                  </button>
                </Col>
              ))}
            </Row>
          </ControlTowerSectionCard>
        </Space>
      )}
    </EducationPageChrome>
  );
}
